/**
 * Render the structured `body` blocks emitted by the blog API.
 *
 * Why not Markdown
 * ----------------
 * We chose a typed block list (`{type, ...}`) over Markdown so the editorial
 * surface is JSX-safe end-to-end: no `dangerouslySetInnerHTML`, no markdown
 * lib in the bundle, no surprise HTML escaping.
 */
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const BlogBody = ({ blocks }) => {
  if (!blocks || blocks.length === 0) return null;
  return (
    <div className="prose-blog space-y-5 text-[#282C2A] leading-relaxed">
      {blocks.map((b, i) => {
        switch (b.type) {
          case "h2":
            return (
              <h2
                key={i}
                className="font-serif-display text-3xl md:text-4xl text-[#282C2A] mt-12 mb-2 leading-tight"
              >
                {b.text}
              </h2>
            );
          case "h3":
            return (
              <h3
                key={i}
                className="font-serif-display text-xl md:text-2xl text-[#282C2A] mt-6 mb-1 leading-snug"
              >
                {b.text}
              </h3>
            );
          case "p":
            return (
              <p key={i} className="text-base md:text-[17px] leading-[1.75] text-[#3B3F3D]">
                {b.text}
              </p>
            );
          case "ul":
            return (
              <ul key={i} className="list-disc pl-6 space-y-2 text-base text-[#3B3F3D]">
                {(b.items || []).map((it, j) => (
                  <li key={j}>{it}</li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol key={i} className="list-decimal pl-6 space-y-2 text-base text-[#3B3F3D]">
                {(b.items || []).map((it, j) => (
                  <li key={j}>{it}</li>
                ))}
              </ol>
            );
          case "quote":
            return (
              <blockquote
                key={i}
                className="border-l-4 border-[#4A5D23] pl-5 my-8 font-serif-display text-xl md:text-2xl italic text-[#282C2A] leading-snug"
              >
                "{b.text}"
              </blockquote>
            );
          case "table":
            return (
              <div key={i} className="overflow-x-auto my-6">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b-2 border-[#282C2A] bg-[#F0EFEB]">
                      {(b.headers || []).map((h, j) => (
                        <th
                          key={j}
                          className="text-left px-3 py-2 font-mono text-xs uppercase tracking-wider text-[#282C2A]"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(b.rows || []).map((r, ri) => (
                      <tr key={ri} className="border-b border-[#E6E4DD]">
                        {r.map((c, ci) => (
                          <td key={ci} className="px-3 py-2.5 font-mono text-[13px] text-[#3B3F3D]">
                            {c}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case "cta":
            return (
              <div
                key={i}
                className="my-10 bg-[#282C2A] text-[#F7F6F2] px-6 py-7 border-2 border-[#282C2A] shadow-paper"
              >
                <div className="font-serif-display text-2xl mb-3">{b.text}</div>
                <Link
                  to={b.href || "/"}
                  data-testid="blog-inline-cta"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#4A5D23] text-white text-xs font-semibold tracking-wider uppercase border border-[#3B4A1C] hover:bg-[#3B4A1C] transition-colors"
                >
                  {b.label || "Vai"}
                  <ArrowRight size={14} strokeWidth={1.75} />
                </Link>
              </div>
            );
          default:
            return null;
        }
      })}
    </div>
  );
};

export default BlogBody;
